/**
 * partialTables — rebuild pipe tables from possibly-truncated markdown.
 *
 * Retrieved source excerpts (citation context, Action History snippets) and
 * streamed model text routinely cut a table anywhere: the excerpt can start
 * mid-table (no header, no `|---|` delimiter), start mid-row, or end mid-row.
 * Strict GFM only builds a <table> from a header + delimiter pair, so those
 * excerpts rendered as a column of literal pipes. This module finds every run
 * of pipe rows, works out the column count robustly (the mode, so a cut first
 * or last row can't skew it) and returns whatever rows survived as a table.
 *
 * Two consumers:
 *   - repairPartialTables(md) — rewrites each run as valid GFM before remark
 *     sees it (AssistantMarkdown, DraftTab, chat bubble, proposal preview).
 *   - splitPipeSegments(text) — the same reconstruction as a text/table segment
 *     list, used by CitationPopover's bespoke table renderer.
 *
 * Rules:
 *   - A run is consecutive lines with ≥2 unescaped pipes, extended by one
 *     line at either edge when that line is a bordered fragment (`| Tot`).
 *     Lines inside ``` fences, list items and blockquotes are never rows.
 *   - Delimiter row present → the row above it is the header. A delimiter
 *     cut short (`|---|--`) is still a delimiter; repeated delimiters inside
 *     the body (seen in backend dumps) are dropped.
 *   - No delimiter → the first row is the header unless it looks like data
 *     (a numeric cell, or a fragment) — then the header is blank and the CSS
 *     hides the empty <thead>, so no data row is promoted to bold.
 *   - First row cut at the start → left-padded with "…" so its values stay
 *     in their columns. Last row cut at the end → right-padded with "…".
 *   - Without a delimiter a run must look like a table (≥2 rows, ≥2 columns,
 *     and ≥3 of one of them, half the rows at the modal width) so prose that
 *     happens to contain pipes is left alone.
 */

export const TRUNCATION_MARK = "…";

export interface PipeTable {
  /** Header cells; all "" when the excerpt had no header row. */
  headers: string[];
  /** Body rows, every one padded to headers.length. */
  rows: string[][];
  /** Per-column alignment delimiter cells (e.g. ":---:"), headers.length long. */
  align: string[];
}

export type PipeSegment = { kind: "text"; text: string } | { kind: "table"; table: PipeTable };

const FENCE_RE = /^\s*(```|~~~)/;
const NOT_A_ROW_RE = /^\s*(?:[-*+]\s|\d+[.)]\s|>)/;
const DELIM_CELL_RE = /^:?-+:?$/;
const NUMERIC_CELL_RE = /^[<>≤≥~±+-]?\s*\(?\d/;

function pipeCount(line: string): number {
  return line.match(/(?<!\\)\|/g)?.length ?? 0;
}

function isRowLine(line: string): boolean {
  return !NOT_A_ROW_RE.test(line) && pipeCount(line) >= 2;
}

/** A one-pipe line hugging a run: a bordered fragment such as `| Total`
 *  (row cut after its first cell) or `cell |` (row cut before its last).
 *  After a tight bare row (`Mean|45.2|44.9`) a tight `SD|3` also counts —
 *  the spacing match keeps "A | B" prose from latching onto a table. */
function isEdgeFragment(line: string, side: "start" | "end", neighbour = ""): boolean {
  const t = line.trim();
  if (NOT_A_ROW_RE.test(t) || pipeCount(t) !== 1) return false;
  if (side === "start") return t.endsWith("|");
  if (t.startsWith("|")) return true;
  const n = neighbour.trim();
  return !n.startsWith("|") && !/\s\|\s/.test(n) && !/\s\|\s/.test(t);
}

interface ParsedRow {
  cells: string[];
  cutStart: boolean;
  cutEnd: boolean;
}

/** Split a row. Bordered runs (`| a | b |`) drop the outer pipes; bare runs
 *  (`Age||`) keep them, because there a trailing `||` is two real empty
 *  columns. A bordered row missing its outer pipe was cut on that side. */
function parseRow(line: string, bordered: boolean): ParsedRow {
  let s = line.trim();
  let cutStart = false;
  let cutEnd = false;
  if (bordered) {
    if (s.startsWith("|")) s = s.slice(1);
    else cutStart = true;
    if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
    else cutEnd = true;
  }
  return { cells: s.split(/(?<!\\)\|/).map((c) => c.trim()), cutStart, cutEnd };
}

function isDelimiter(cells: string[]): boolean {
  return cells.length > 0 && cells.every((c) => DELIM_CELL_RE.test(c));
}

function modeOf(counts: number[]): number {
  const freq = new Map<number, number>();
  for (const c of counts) freq.set(c, (freq.get(c) ?? 0) + 1);
  let best = 0;
  let bestFreq = 0;
  for (const [count, f] of freq) {
    if (f > bestFreq || (f === bestFreq && count > best)) {
      best = count;
      bestFreq = f;
    }
  }
  return best;
}

const blankRow = (n: number): string[] => Array(n).fill("");

function padRight(cells: string[], n: number, mark: boolean): string[] {
  if (cells.length >= n) return cells.slice(0, n);
  const out = cells.concat(blankRow(n - cells.length));
  if (mark) out[cells.length] = TRUNCATION_MARK;
  return out;
}

function padLeft(cells: string[], n: number): string[] {
  if (cells.length >= n) return cells.slice(cells.length - n);
  return [TRUNCATION_MARK, ...blankRow(n - cells.length - 1), ...cells];
}

/** Rebuild one run of pipe lines into zero or more tables (a run can hold
 *  the tail of one table and the head of the next). Null = not a table. */
function reconstructRun(lines: string[]): PipeTable[] | null {
  const startsBordered = lines.filter((l) => l.trim().startsWith("|")).length;
  const bordered = startsBordered * 2 >= lines.length;
  const parsed = lines.map((l) => parseRow(l, bordered));

  const firstDelim = parsed.findIndex((r) => isDelimiter(r.cells));
  // Rows above the header row belong to an earlier, headerless table.
  if (firstDelim > 1) {
    const before = reconstructRun(lines.slice(0, firstDelim - 1));
    const after = reconstructRun(lines.slice(firstDelim - 1));
    if (!after) return before;
    return before ? [...before, ...after] : after;
  }

  const delimRow = firstDelim >= 0 ? parsed[firstDelim] : null;
  const headerRow = firstDelim === 1 ? parsed[0] : null;
  const body = parsed.filter((r, i) => !isDelimiter(r.cells) && !(headerRow && i === 0));
  if (body.length === 0) return null;

  // Column count: the modal row width, widened to any interior row (GFM would
  // silently drop the extra cells) and to the header/delimiter when present.
  const interior = body.slice(1, -1).map((r) => r.cells.length);
  const fullRows = body.filter((r) => !r.cutStart && !r.cutEnd).map((r) => r.cells.length);
  const modal = modeOf(fullRows.length > 0 ? fullRows : body.map((r) => r.cells.length));
  const colCount = Math.max(
    modal,
    ...interior,
    headerRow?.cells.length ?? 0,
    delimRow && !delimRow.cutEnd ? delimRow.cells.length : 0,
  );
  if (colCount < 2) return null;

  if (!delimRow) {
    const atWidth = body.filter((r) => r.cells.length === colCount).length;
    const enough = body.length >= 2 && (colCount >= 3 || body.length >= 3);
    if (!enough || atWidth * 2 < body.length) return null;
  }

  const rows = body.map((r, i) => {
    const isFirst = i === 0 && !headerRow;
    const isLast = i === body.length - 1;
    if (r.cells.length < colCount && isFirst && (r.cutStart || !bordered)) return padLeft(r.cells, colCount);
    if (r.cells.length > colCount && isFirst && r.cutStart) return padLeft(r.cells, colCount);
    return padRight(r.cells, colCount, isLast && (r.cutEnd || r.cells.length < colCount));
  });

  let headers: string[];
  if (headerRow) {
    headers = padRight(headerRow.cells, colCount, false);
  } else if (!delimRow && looksLikeHeader(body[0], bordered, colCount)) {
    headers = rows.shift() as string[];
    if (rows.length === 0) return null;
  } else {
    headers = blankRow(colCount);
  }

  const align = padRight(delimRow?.cells.filter((c) => c !== "") ?? [], colCount, false).map((c) =>
    DELIM_CELL_RE.test(c) ? c : "---",
  );
  return [{ headers, rows, align }];
}

function looksLikeHeader(row: ParsedRow, bordered: boolean, colCount: number): boolean {
  if (row.cutStart || (bordered && row.cutEnd) || row.cells.length !== colCount) return false;
  return !row.cells.some((c) => NUMERIC_CELL_RE.test(c));
}

/** Split markdown into ordered text / table segments. Text segments keep
 *  their original lines verbatim (including blank lines). */
export function splitPipeSegments(md: string): PipeSegment[] {
  const lines = md.split(/\r?\n/);
  const segments: PipeSegment[] = [];
  let textBuf: string[] = [];
  const flushText = () => {
    if (textBuf.length > 0) segments.push({ kind: "text", text: textBuf.join("\n") });
    textBuf = [];
  };

  let inFence = false;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (FENCE_RE.test(line)) inFence = !inFence;
    const startsRun =
      !inFence &&
      (isRowLine(line) || (isEdgeFragment(line, "start") && i + 1 < lines.length && isRowLine(lines[i + 1])));
    if (!startsRun) {
      textBuf.push(line);
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < lines.length && isRowLine(lines[j])) j += 1;
    if (j < lines.length && isEdgeFragment(lines[j], "end", lines[j - 1])) j += 1;

    const tables = reconstructRun(lines.slice(i, j));
    if (tables) {
      flushText();
      for (const table of tables) segments.push({ kind: "table", table });
    } else {
      textBuf.push(...lines.slice(i, j));
    }
    i = j;
  }
  flushText();
  return segments;
}

const escapeCell = (cell: string): string => cell.replace(/(?<!\\)\|/g, "\\|");
const gfmRow = (cells: string[]): string => `| ${cells.map(escapeCell).join(" | ")} |`;

export function pipeTableToGfm(table: PipeTable): string {
  return [gfmRow(table.headers), `| ${table.align.join(" | ")} |`, ...table.rows.map(gfmRow)].join("\n");
}

/**
 * Rewrite every pipe-row run in `md` as a valid GFM table (blank line before
 * and after), keeping whatever rows a truncated excerpt still holds. Text
 * outside tables is untouched; complete tables come back with the same cells.
 * Idempotent, so it is safe on every render of a streaming string.
 */
export function repairPartialTables(md: string): string {
  if (!md.includes("|")) return md;
  const segments = splitPipeSegments(md);
  if (!segments.some((s) => s.kind === "table")) return md;
  const out: string[] = [];
  const lastIsBlank = () => out.length === 0 || out[out.length - 1].trim() === "";
  for (const seg of segments) {
    if (seg.kind === "text") {
      const textLines = seg.text.split("\n");
      // A table already ends with a blank line; don't double it.
      if (lastIsBlank() && textLines[0].trim() === "") textLines.shift();
      out.push(...textLines);
      continue;
    }
    if (!lastIsBlank()) out.push("");
    out.push(pipeTableToGfm(seg.table), "");
  }
  // The blank after a final table is only kept if the input ended with one.
  if (out[out.length - 1] === "" && !md.endsWith("\n")) out.pop();
  return out.join("\n");
}
